import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useVoiceFieldFocus } from "hooks/useVoiceFieldFocus";
import { ACTIONS, STEPS } from "../config";
import { FieldWithEntitySelector } from "components/FieldWithEntitySelector";
import { Species } from "types/species";
import { Site } from "types/site";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import { ErrorType, RequestFeedbackData } from "../types";
import { RequestFeedbackSteps } from "helpers/constants";
import { FeedbackStatus } from "types/feedback";
import { useNavigate } from "react-router-dom";
import { CoachRoutes } from "config/routes";
import { REQUIREMENT } from "helpers/constants";

type RequestPatientAdditionalInfoStepProps = {
  onUpdate: (data: any) => void;
  data: RequestFeedbackData;
  error: ErrorType;
  species: Species[];
  status: FeedbackStatus | null;
  sites: Site[];
  handleStep: (step: string, action?: string) => void;
};

export const RequestPatientAdditionalInfoStep: FC<RequestPatientAdditionalInfoStepProps> = ({
  onUpdate,
  data,
  species,
  sites,
  error,
  status,
  handleStep,
}) => {
  const isMissingInfo = status === FeedbackStatus.MISSING_INFO;
  const navigate = useNavigate();
  const { activeFieldId } = useVoiceFieldFocus();
  const [sexOpen, setSexOpen] = useState(false);
  const sexVoiceOpen = activeFieldId === "animalSex";
  
  const isITER = useMemo(
    () => data?.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER,
    [data?.selectedCompetencySkill?.type],
  );

  const backStep = useMemo(() => {
    return isITER ? STEPS.REQUEST2 : STEPS.REQUEST;
  }, [isITER]);

  return (
    <StepLayoutContainer
      title="Please Identify the Patient"
      onClickBack={() =>
        isMissingInfo
          ? navigate(CoachRoutes.FeedbackPortal)
          : handleStep(backStep, "back")
      }
      onClickNext={() => handleStep(STEPS.PERFORM)}
      nextButtonText={isMissingInfo ? "Save Missing Info" : undefined}
    >
      <Box>
        <Typography>{RequestFeedbackSteps.RequestText2}</Typography>
        <Box mt={2}>
          <FieldWithEntitySelector
            label="Species"
            voiceFieldId="selectedSpecies"
            element={data.selectedSpecies}
            entityList={species}
            entityName="Species"
            idField="species_id"
            onChangeElement={(element) => {
              onUpdate({
                type: ACTIONS.SET_SPECIES,
                payload: element,
              });
            }}
            error={error.selectedSpecies ?? ""}
          />
        </Box>

        <Box mt={3}>
          <FormControl fullWidth>
            <InputLabel id="animalSex">Animal Sex</InputLabel>
            <Select
              labelId="animalSex"
              id="animalSex-select"
              size="small"
              value={data.animalSex}
              label="Animal Sex"
              open={sexVoiceOpen || sexOpen}
              onOpen={() => setSexOpen(true)}
              onClose={() => {
                if (!sexVoiceOpen) setSexOpen(false);
              }}
              onChange={(event: SelectChangeEvent) =>
                onUpdate({
                  payload: event.target.value,
                  type: ACTIONS.SET_SEX,
                })
              }
              error={!!error.animalSex}
            >
              <MenuItem value={"Female"}>Female</MenuItem>
              <MenuItem value={"Female Spayed"}>Female Spayed</MenuItem>
              <MenuItem value={"Male"}>Male</MenuItem>
              <MenuItem value={"Male Castrated"}>Male Castrated</MenuItem>
            </Select>
            {error.animalSex && (
              <FormHelperText error>{error.animalSex}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box mt={3}>
          <FieldWithEntitySelector
            disabled={sites.length === 0}
            label="Site"
            voiceFieldId="selectedSite"
            element={data.selectedSite}
            entityList={sites}
            entityName="Site"
            idField="site_id"
            onChangeElement={(element) => {
              onUpdate({
                type: ACTIONS.SET_SITE,
                payload: element,
              });
            }}
          />
        </Box>

        <Box mt={2}>
          <TextField
            label="Course or Specialty"
            name="course"
            value={data.course}
            onChange={(e) =>
              onUpdate({ type: ACTIONS.SET_COURSE, payload: e.target.value })
            }
            margin="dense"
            fullWidth
            type="text"
            size="small"
            error={!!error.course}
            helperText={error.course}
          />
        </Box>
      </Box>
    </StepLayoutContainer>
  );
};

